// In the app (R-1: the old admin's Lender → In the app tab, now in the new portal; App mode was removed in S-1).
// Routes: api2/src/modules/in-app (api-contract §6a); shape: firstfund-contracts/schemas/hub/in-app. On the
// fixtures (npm test) there is nothing behind them: the screens say they need the Lender Hub API.
import { api, apiMode } from '@/lib/hubApi'
import { readFile } from '@/lib/httpClient'

export type CheckName = 'product' | 'kfs' | 'grievance' | 'integration' | 'offer_check' | 'sign_off'
export interface AppCheck { name: CheckName; label: string; result: 'pass' | 'fail' | null; detail: string | null; checkedAt: string | null; signedOffBy: string | null }
export interface Handover { web_app_url: string | null; app_android_url: string | null; app_ios_url: string | null; button_texts: Record<string, string> }
export interface InApp {
  lender: { slug: string; name: string; gatewayLender: string; kind: 'bank' | 'nbfc' | null; regulatedBy: string; hasLogo: boolean; appStatus: 'off' | 'live' | 'paused'; appLiveAt: string | null; appPausedAt: string | null }
  /** tab 6's own fields, named as in api/hub-lenders-catalog.v1 */
  product: Record<string, unknown>
  /** the facts read from the lender's page (PF-3): tab 2's first product, tab 1's grievance officer */
  facts: Record<string, unknown>
  /** the tab 2 product the facts come from */
  factsFrom: { slug: string; name: string } | null
  version: number
  kfs: { version: string | null; uploadedAt: string | null } | null
  updatedAt: string | null
  publishedVersion: number | null
  publishedAt: string | null
  changesNotPublished: boolean
  missing: string[]
  handover: (Handover & { name: string }) | null
  integrations: { integrationId: string; title: string; enabled: boolean; health: string; hasHandover: boolean }[]
  gatewayError: string | null
  checks: AppCheck[]
  storage: boolean
}

/** The old Hub's limits (api2/src/config/rules.ts IN_APP). */
export const KFS_MAX_BYTES = 5 * 1024 * 1024
export const LOGO_MAX_BYTES = 512 * 1024
export const WORK_TYPES: [string, string][] = [['salaried', 'Salaried'], ['self_employed_business', 'Self-employed business'], ['self_employed_professional', 'Self-employed professional']]
/** The button texts a handover may set (the old journey rules' BUTTONS); empty = the app's own words. */
export const BUTTONS: [string, string][] = [['apply', 'Apply'], ['offer', 'Offer'], ['redirect', 'Go to the lender'], ['web', 'Continue on the web'], ['app_android', 'Android app'], ['app_ios', 'iPhone app'], ['retry', 'Try again']]

const needsApi = () => { throw new Error('In the app needs the Lender Hub API (the fixtures have no app catalog).') }
const base = (slug: string) => `/lenders/${slug}/app`

/** A picked file as base64, its size checked before it is sent. */
async function base64Of(f: File, max: number) {
  if (f.size > max) throw new Error(`${f.name} is ${Math.round(f.size / 1024)} KB: ${Math.round(max / 1024)} KB at most`)
  return (await readFile(f)).base64
}

export const inApp = {
  available: () => apiMode(),
  get: (slug: string) => (apiMode() ? api<InApp>('GET', base(slug)) : needsApi()),
  saveProduct: (slug: string, body: Record<string, unknown>) => api<InApp>('PUT', `${base(slug)}/product`, body),
  saveHandover: (slug: string, h: Handover) => api<InApp>('PUT', `${base(slug)}/handover`, h),
  uploadKfs: async (slug: string, f: File, version: string) => api<InApp>('PUT', `${base(slug)}/kfs`, { version, base64: await base64Of(f, KFS_MAX_BYTES) }),
  uploadLogo: async (slug: string, f: File) => api<InApp>('PUT', `${base(slug)}/logo`, { base64: await base64Of(f, LOGO_MAX_BYTES) }),
  /** the current file, opened in a new tab (the session cookie goes with it) */
  fileUrl: (slug: string, which: 'kfs' | 'logo', v?: number) => `${String(import.meta.env.VITE_HUB_API_BASE ?? '')}/admin/v1${base(slug)}/${which}${v ? `?v=${v}` : ''}`,
  run: (slug: string) => api<InApp>('POST', `${base(slug)}/checks/run`),
  signOff: (slug: string, note: string) => api<InApp>('POST', `${base(slug)}/sign-off`, { note }),
  live: (slug: string) => api<InApp>('POST', `${base(slug)}/live`),
  publish: (slug: string) => api<InApp>('POST', `${base(slug)}/publish`),
  pause: (slug: string, reason: string) => api<InApp>('POST', `${base(slug)}/pause`, { reason }),
}

const INTS = ['cooling_off_days']
const DECIMALS = ['processing_fee_pct_max']
const TEXTS = ['penal_charges_text', 'foreclosure_text', 'offer_mode', 'integration_id', 'pincode_mode']

/**
 * Tab 6's form as the API wants it (the old admin's formBody, PF-3: only tab 6's own fields; the facts are the lender
 * page's): numbers as numbers, empty boxes as "" (= not set), lists as arrays.
 */
export function productBody(fd: FormData): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const k of [...INTS, ...DECIMALS]) body[k] = fd.get(k) === '' || fd.get(k) === null ? '' : Number(fd.get(k))
  for (const k of TEXTS) body[k] = String(fd.get(k) ?? '').trim()
  body.work_types = fd.getAll('work_types').map(String)
  body.pincode_list = String(fd.get('pincode_list') ?? '').split(/[\s,]+/).filter(Boolean)
  return body
}
